import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import SearchableSelect from '../../components/SearchableSelect';
import { MHE_LIST, CURRENCY_LIST, MHE_PRICING_MODES } from '../../constants/serviceTypes';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Forklift } from 'lucide-react';

const ProviderAddMHE = () => {
  const [formData, setFormData] = useState({
    mhe_type: '',
    name: '',
    description: '',
    quantity: 1,
    condition: 'good',
    year_of_manufacture: '',
    location_city: '',
    location_state: '',
    location_country: '',
    pricing_mode: 'daily',
    rate: '',
    currency: 'USD',
    available: true
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.mhe_type) {
      toast.error('Please select an MHE type');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/provider/mhe`, {
        ...formData,
        quantity: parseInt(formData.quantity) || 1,
        rate: parseFloat(formData.rate) || 0,
        year_of_manufacture: formData.year_of_manufacture ? parseInt(formData.year_of_manufacture) : null
      });
      toast.success('MHE added successfully');
      navigate('/provider/mhe');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add MHE');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex flex-col">
      <Navbar />
      <div className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 bg-amber-100 rounded-sm flex items-center justify-center">
              <Forklift className="h-5 w-5 text-amber-600" />
            </div>
            <h1 className="font-heading text-3xl font-black text-foreground tracking-tight">
              Add MHE
            </h1>
          </div>
          <p className="text-muted-foreground">List material handling equipment for rent</p>
        </div>

        <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="add-mhe-form">
            <div className="space-y-4">
              <SearchableSelect
                options={MHE_LIST}
                value={formData.mhe_type}
                onChange={(value) => setFormData({ ...formData, mhe_type: value })}
                placeholder="Search and select MHE type..."
                label="Equipment Type *"
                testId="mhe-type-select"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Listing Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Forklift for Rent - Downtown"
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quantity">Quantity Available *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the equipment condition, features, etc."
                  rows={3}
                  className="rounded-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="condition">Condition</Label>
                  <Select value={formData.condition} onValueChange={(value) => setFormData({ ...formData, condition: value })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="excellent">Excellent</SelectItem>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="year">Year of Manufacture</Label>
                  <Input
                    id="year"
                    type="number"
                    min="1990"
                    max={new Date().getFullYear()}
                    value={formData.year_of_manufacture}
                    onChange={(e) => setFormData({ ...formData, year_of_manufacture: e.target.value })}
                    placeholder="e.g., 2020"
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-6 space-y-4">
              <h3 className="font-heading text-lg font-bold text-foreground">Location</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.location_city}
                    onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.location_state}
                    onChange={(e) => setFormData({ ...formData, location_state: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country *</Label>
                  <Input
                    id="country"
                    value={formData.location_country}
                    onChange={(e) => setFormData({ ...formData, location_country: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-6 space-y-4">
              <h3 className="font-heading text-lg font-bold text-foreground">Pricing</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Pricing Mode</Label>
                  <Select value={formData.pricing_mode} onValueChange={(value) => setFormData({ ...formData, pricing_mode: value })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MHE_PRICING_MODES.map((mode) => (
                        <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_LIST.map((curr) => (
                        <SelectItem key={curr.value} value={curr.value}>{curr.value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rate">Rate *</Label>
                  <Input
                    id="rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.rate}
                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-6 border-t border-border">
              <Button
                type="submit"
                disabled={loading}
                className="bg-amber-600 text-white hover:bg-amber-700 rounded-sm"
              >
                {loading ? 'Adding...' : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Add MHE
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/provider/dashboard')}
                className="rounded-sm"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default ProviderAddMHE;
